/**
 * The 207 body of `POST vesselJobs/completeJob` (CHR-1078 ADR-0127, deviation D1).
 *
 * On the workflow-kernel path each instance transitions through its own durable workflow, so the endpoint
 * decides each item on its own and answers `207 Multi-Status` when some were refused. Every field is
 * optional here on purpose: the retained legacy driver answers the same `BulkApprovalResult` but refuses a
 * whole batch by throwing, so it only ever reaches this parser with `FailedCount` unset - and an unset
 * count parses to `null` (nothing refused), which is the correct answer for that branch.
 */
export interface BulkCompletionResultBody {
  SuccessCount?: number;
  FailedCount?: number;
  FailedIds?: string[];
  FailedReasons?: Record<string, string>;
}

/**
 * The refusal reason a completion response carries, or `null` when nothing was refused.
 *
 * **Why the body and not the status code.** 207 is a 2xx, so axios resolves it and every `await` succeeds —
 * which is exactly how a refused completion used to end in a success toast. `FailedCount` is the authority;
 * the reason is the server's own operator-facing sentence (the backend deliberately puts only client-safe
 * text in this body), returned verbatim so the operator learns what to fix rather than "something failed".
 *
 * An empty string means "refused, but the server named no reason" — a distinct answer from `null`, which
 * means "nothing was refused". Both matter: only `null` may take the success path.
 */
export const readBulkCompletionFailure = (body: unknown): string | null => {
  if (typeof body !== 'object' || body === null) {
    return null;
  }

  const result = body as BulkCompletionResultBody;
  if (typeof result.FailedCount !== 'number' || result.FailedCount <= 0) {
    return null;
  }

  const reasons = Object.values(result.FailedReasons ?? {});
  return reasons[0] ?? '';
};
